import { DataTable } from "primereact/datatable";
import "./billing-contract.scss";
import { useState } from "react";
import { GetResponseBillingModel } from "@factory/model/query-model-billing";
import { useQyGetBilling } from "@factory/query/billing.query";
import { ContractFormSchema } from "@factory/form/form.rule";
import { useFormContext } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Column } from "primereact/column";
import { currencyFormat } from "@shared/utility/formats/currency-format";
import { isEmpty } from "lodash-es";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { Tag } from "primereact/tag";
import { getPaymentSeverity } from "@core/utility/get-severity";
import {
  canInitiatePayment,
  getPaymentBlockedReason,
} from "@core/utility/billing-financials";
import { Button } from "primereact/button";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import DashboardCard from "@shared/ui/dashboard-card/dashboard-card";
import { FaCoins, FaMoneyBillWave, FaWallet, FaTags } from "react-icons/fa";
import { DiscountManagementDialog } from "./discount-management-dialog";

/* eslint-disable-next-line */
export interface BillingContractProps {}

export function BillingContract() {
  const navigate = useNavigate();
  const { setValue } = useFormContext<ContractFormSchema>();
  const { formatDateLocal } = useDateTimeContext();
  const { contractId } = useParams();
  const { showInfo } = useNotificationContext();
  const [discountDialogBillingCode, setDiscountDialogBillingCode] = useState<
    string | null
  >(null);

  // API
  const handleGetApiBillingSuccess = (response: GetResponseBillingModel) => {
    const billingData = response?.data;
    setValue("session", billingData?.length ?? 0);
  };
  const { data: billingResponse } = useQyGetBilling(
    "",
    9999,
    0,
    undefined,
    {
      contract: contractId || "",
    },
    true,
    handleGetApiBillingSuccess
  );

  const billingData = billingResponse?.data || [];

  // LOCAL FUNCTION
  const handlePay = () => {
    navigate(`../pay`, {
      state: {
        balance: billingData?.[0]?.total_balance ?? 0,
        isBulk: true,
      },
    });
  };
  const handlePaySession = (
    billingId?: string,
    status?: string,
    balance?: number
  ) => {
    if (!billingId) return;

    const blockedReason = getPaymentBlockedReason(status, balance);
    if (blockedReason) {
      showInfo(blockedReason);
      return;
    }

    navigate(`../pay-session?billingId=${billingId}`, {
      state: {
        balance: balance ?? 0,
        isBulk: false,
      },
    });
  };

  // LOCAL RENDER
  const grid = (
    <DataTable
      className="mb-6"
      value={billingData}
      tableStyle={{ minWidth: "50rem" }}
    >
      <Column field="session" header="Session" sortable></Column>
      <Column
        field="original_amount"
        header="Original"
        body={(cell) =>
          currencyFormat(cell.original_amount ?? cell.amount, "PHP")
        }
      ></Column>
      <Column
        field="total_discount"
        header="Discount"
        body={(cell) => currencyFormat(cell.total_discount ?? 0, "PHP")}
      ></Column>
      <Column
        field="amount_paid"
        header="Amount Paid"
        body={(cell) => currencyFormat(cell.amount_paid, "PHP")}
      ></Column>
      <Column
        field="balance"
        header="Balance"
        body={(cell) =>
          cell.has_overpayment && cell.credit_balance > 0
            ? `Credit ${currencyFormat(cell.credit_balance, "PHP")}`
            : currencyFormat(cell.balance, "PHP")
        }
      ></Column>
      <Column
        field="payment_date"
        header="Date Paid"
        body={(cell) => {
          if (isEmpty(cell.payment_date)) {
            return "-";
          }
          return formatDateLocal(cell.payment_date);
        }}
      ></Column>
      <Column field="provider_name" header="Provider" sortable></Column>
      <Column
        field="status"
        header="Status"
        body={(cell) => (
          <Tag
            value={cell?.status}
            severity={getPaymentSeverity(cell?.status || "")}
          />
        )}
      ></Column>
      <Column
        body={(rowData) => (
          <div className="flex gap-2">
            <Button
              label="Pay Session"
              severity="secondary"
              outlined
              disabled={!canInitiatePayment(rowData?.status, rowData?.balance)}
              onClick={() =>
                handlePaySession(rowData?.code, rowData?.status, rowData?.balance)
              }
            />
            <Button
              label="Discount"
              severity="help"
              text
              icon="pi pi-percentage"
              onClick={() => setDiscountDialogBillingCode(rowData?.code)}
            />
          </div>
        )}
        header="Action"
      />
    </DataTable>
  );

  const summary = billingData?.length > 0 && (
    <div className="flex flex-wrap gap-4 mb-6">
      <div className="flex-1 min-w-[250px]">
        <DashboardCard
          model={{
            title: "Total Contract Amount",
            value: currencyFormat(billingData[0].contract_amount, "PHP"),
            icon: <FaMoneyBillWave className="text-gray-600 text-2xl" />,
            iconClass: "bg-gray-100",
            description: "",
          }}
        />
      </div>

      <div className="flex-1 min-w-[250px]">
        <DashboardCard
          model={{
            title: "Total Discount",
            value: currencyFormat(
              billingData[0].total_discount_in_contract ?? 0,
              "PHP"
            ),
            icon: <FaTags className="text-orange-600 text-2xl" />,
            iconClass: "bg-orange-100",
            description: "",
          }}
        />
      </div>

      <div className="flex-1 min-w-[250px]">
        <DashboardCard
          model={{
            title: "Total Amount Paid",
            value: currencyFormat(billingData[0].total_amount_paid, "PHP"),
            icon: <FaCoins className="text-green-600 text-2xl" />,
            iconClass: "bg-green-100",
            description: "",
          }}
        />
      </div>

      <div className="flex-1 min-w-[250px]">
        <DashboardCard
          model={{
            title: "Remaining Balance",
            value: currencyFormat(billingData[0].total_balance, "PHP"),
            icon: <FaWallet className="text-red-500 text-2xl" />,
            iconClass: "bg-red-100",
            description: "",
          }}
        />
      </div>
    </div>
  );

  return (
    <div id="BillingContract" className="billing-contract">
      {summary}
      <div className="flex justify-between items-center mb-4">
        <h5 className="mb-2">
          Total: <b>{billingResponse?.count}</b>
        </h5>

        <Button
          label="Custom Pay"
          outlined
          disabled={(billingData?.[0]?.total_balance ?? 0) <= 0}
          onClick={handlePay}
        />
      </div>
      {grid}
      <DiscountManagementDialog
        visible={!!discountDialogBillingCode}
        onHide={() => setDiscountDialogBillingCode(null)}
        billing={billingData.find((b) => b.code === discountDialogBillingCode)}
      />
    </div>
  );
}

export default BillingContract;
